import type { Job, JobStatus } from '@workflow/contracts'
import { cn } from '@workflow/ui'

type Glyph = JobStatus | 'ready'

/**
 * A job's state as one small mark, drawn in ink alone — the shape carries the
 * meaning, not a colour: a dashed ring for a draft, an open ring for
 * published, half a disc for work under way, two bars for a pause, a ring
 * and check when every player is done and the job waits only to be closed,
 * a solid disc and check for completed, a struck ring for removed, a crossed
 * and faded one for cancelled. Progress is the bar's to tell; the mark says
 * only where the job stands.
 */
export function JobStatusGlyph({ status, className }: { status: Glyph; className?: string }) {
  const ring = (stroke: string, extra?: Record<string, string | number>) => (
    <circle cx="10" cy="10" r="8" fill="none" stroke={stroke} strokeWidth="1.6" {...extra} />
  )
  const mark = (() => {
    switch (status) {
      case 'draft':
        return ring('var(--muted-foreground)', { strokeDasharray: '2.6 2.4', opacity: 0.8 })
      case 'published':
        return ring('var(--foreground)')
      case 'in_progress':
        return (
          <>
            {ring('var(--foreground)')}
            <path d="M10 10V4.6a5.4 5.4 0 0 1 0 10.8Z" fill="var(--foreground)" />
          </>
        )
      case 'on_hold':
        return (
          <>
            {ring('var(--foreground)')}
            <rect x="7.1" y="6.6" width="2.1" height="6.8" rx="1.05" fill="var(--foreground)" />
            <rect x="10.8" y="6.6" width="2.1" height="6.8" rx="1.05" fill="var(--foreground)" />
          </>
        )
      case 'ready':
        return (
          <>
            {ring('var(--foreground)')}
            <path d="M6.6 10.3l2.3 2.3 4.5-4.8" fill="none" stroke="var(--foreground)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )
      case 'completed':
        return (
          <>
            <circle cx="10" cy="10" r="8.8" fill="var(--foreground)" />
            <path d="M6.6 10.3l2.3 2.3 4.5-4.8" fill="none" stroke="var(--page)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )
      case 'removed':
        return (
          <>
            {ring('var(--muted-foreground)')}
            <path d="M6.5 10h7" stroke="var(--muted-foreground)" strokeWidth="1.8" strokeLinecap="round" />
          </>
        )
      case 'cancelled':
        return (
          <g opacity="0.7">
            {ring('var(--muted-foreground)')}
            <path d="M7.6 7.6l4.8 4.8M12.4 7.6l-4.8 4.8" stroke="var(--muted-foreground)" strokeWidth="1.7" strokeLinecap="round" />
          </g>
        )
    }
  })()
  return (
    <svg aria-hidden viewBox="0 0 20 20" className={cn('size-5 shrink-0', className)}>
      {mark}
    </svg>
  )
}

/** Which mark a job shows: its status, or `ready` when only the studio's close remains. */
export const glyphOf = (job: Job): Glyph => (job.readyToComplete ? 'ready' : job.status)
